import { normalizeSearch } from "@/shared/lib/search";

/* Comment bodies are plain text in which a mention is a token: @[Nour Hassan](user:<id>).
   People never see or type tokens: the composer shows "@Nour Hassan" and turns it back into a
   token when the comment is sent (encodeMentions). */

const MENTION_TOKEN = /@\[([^\]\n]+)\]\(user:([0-9A-Za-z-]+)\)/g;
/* A name ends where a letter, digit or mark does not follow, so "@Nour" never matches "@Nourhan". */
const WORD_CHARACTER = /[\p{L}\p{N}\p{M}_]/u;
const MAX_QUERY_LENGTH = 40;
const MAX_QUERY_SPACES = 2;

export interface MentionedPerson {
  id: string;
  name: string;
}

export type CommentSegment =
  { kind: "text"; text: string } | { kind: "mention"; id: string; name: string };

/** The body in order: plain text and mentions, ready to render mentions as chips. */
export function parseCommentBody(body: string): CommentSegment[] {
  const segments: CommentSegment[] = [];
  let last = 0;
  for (const match of body.matchAll(MENTION_TOKEN)) {
    const [token, name = "", id = ""] = match;
    if (match.index > last) segments.push({ kind: "text", text: body.slice(last, match.index) });
    segments.push({ kind: "mention", id, name });
    last = match.index + token.length;
  }
  if (last < body.length) segments.push({ kind: "text", text: body.slice(last) });
  return segments;
}

/** The body as people read and edit it: every token becomes "@Name". */
export function toReadableText(body: string): string {
  return body.replace(MENTION_TOKEN, (_token, name: string) => `@${name}`);
}

/** The people a body mentions, each once, in order of appearance. */
export function mentionsIn(body: string): MentionedPerson[] {
  const seen = new Map<string, MentionedPerson>();
  for (const segment of parseCommentBody(body)) {
    if (segment.kind === "mention" && !seen.has(segment.id)) {
      seen.set(segment.id, { id: segment.id, name: segment.name });
    }
  }
  return [...seen.values()];
}

export function mentionToken(person: MentionedPerson): string {
  return `@[${person.name}](user:${person.id})`;
}

function isWordCharacter(character: string | undefined): boolean {
  return character !== undefined && WORD_CHARACTER.test(character);
}

/** The person whose "@Name" starts at `at` (the @ sign), if any. */
function personAt(
  text: string,
  at: number,
  people: readonly MentionedPerson[],
): MentionedPerson | undefined {
  if (isWordCharacter(text[at - 1])) return undefined; // an email address, not a mention
  return people.find(
    (person) =>
      text.startsWith(person.name, at + 1) && !isWordCharacter(text[at + 1 + person.name.length]),
  );
}

/**
 * Turn every "@Name" of the given people back into a token. People earlier in the list win a
 * tie (pass the ones picked from the suggestions first), and longer names are tried first, so
 * "@Nour Hassan" is not read as "@Nour" followed by " Hassan".
 */
export function encodeMentions(text: string, people: readonly MentionedPerson[]): string {
  const candidates = people
    .filter((person) => person.name.trim() !== "")
    .map((person, order) => ({ person, order }))
    .sort((a, b) => b.person.name.length - a.person.name.length || a.order - b.order)
    .map(({ person }) => person);
  let result = "";
  let index = 0;
  while (index < text.length) {
    const person = text[index] === "@" ? personAt(text, index, candidates) : undefined;
    if (person) {
      result += mentionToken(person);
      index += 1 + person.name.length;
    } else {
      result += text[index] ?? "";
      index += 1;
    }
  }
  return result;
}

export interface MentionQuery {
  /** Where the @ sign is. */
  start: number;
  /** What follows it up to the caret, such as "nou". */
  query: string;
}

/**
 * The "@name" being typed at the caret, if any. The @ starts the text or follows a space or a
 * line break; the query may hold a space or two (names have several words) but no line break.
 */
export function mentionQueryAt(text: string, caret: number): MentionQuery | null {
  const before = text.slice(0, caret);
  const start = before.lastIndexOf("@");
  if (start === -1 || isWordCharacter(before[start - 1])) return null;
  const query = before.slice(start + 1);
  const spaces = query.split(" ").length - 1;
  if (query.length > MAX_QUERY_LENGTH || spaces > MAX_QUERY_SPACES) return null;
  if (/[\n@]/.test(query) || query.startsWith(" ")) return null;
  return { start, query };
}

/** Replace the "@query" being typed with "@Name " and put the caret after it. */
export function insertMention(
  text: string,
  mention: MentionQuery,
  name: string,
): { text: string; caret: number } {
  const inserted = `@${name} `;
  const end = mention.start + 1 + mention.query.length;
  const rest = text.slice(end).replace(/^ /, "");
  return {
    text: text.slice(0, mention.start) + inserted + rest,
    caret: mention.start + inserted.length,
  };
}

/** The suggestions offered at most at once. */
export const SUGGESTION_LIMIT = 8;

/**
 * People whose name matches what follows the @, names starting with it first, folding Arabic
 * spellings (normalizeSearch) so "@احمد" finds "أحمد".
 */
export function matchingPeople<T extends MentionedPerson>(
  people: readonly T[],
  query: string,
): T[] {
  const needle = normalizeSearch(query);
  const scored = people
    .map((person) => ({ person, name: normalizeSearch(person.name) }))
    .filter(({ name }) => needle === "" || name.includes(needle))
    .map(({ person, name }) => ({
      person,
      rank: name.startsWith(needle) || name.includes(` ${needle}`) ? 0 : 1,
    }));
  return scored
    .sort((a, b) => a.rank - b.rank)
    .slice(0, SUGGESTION_LIMIT)
    .map(({ person }) => person);
}
